import { create } from "zustand";
import { api, ApiError, setCsrfToken } from "@teamblob/api-client";
import type {
  AuthResponse,
  LoginPayload,
  RegisterPayload,
  RegisterResponse,
  CreateWorkspacePayload,
  CreateWorkspaceResponse,
  TenantInfo,
  UserProfile,
} from "../types/auth.js";

// ---------------------------------------------------------------------------
// Session check: what counts as "signed out"
// ---------------------------------------------------------------------------

/**
 * Only a real 401/403 from the API means the session is gone. Anything else
 * (a 502 while the API restarts on a deploy, a network error, an HTML error
 * page that isn't JSON) says nothing about the session, so the user stays
 * where they are and the check is retried.
 */
export function isSignedOutError(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    (err.statusCode === 401 || err.statusCode === 403)
  );
}

/**
 * Waits between retries while the API can't be reached: about a minute in
 * all, which covers an API restart on a deploy (33 s on the local stack).
 * Capped at 5 s so the app comes back within 5 s of the API. After the last
 * one the notice offers "Try again" instead.
 */
export const SESSION_RETRY_DELAYS_MS: readonly number[] = [
  1_000,
  2_000,
  4_000,
  ...Array<number>(10).fill(5_000),
];

/**
 * Whether the session check is getting answers from the API.
 *  - `online`: the last check got an answer (signed in or signed out).
 *  - `reconnecting`: the API didn't answer; retrying on the schedule above.
 *  - `unreachable`: the retries ran out; waiting for the user to try again.
 */
export type ConnectionState = "online" | "reconnecting" | "unreachable";

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------

interface AuthState {
  user: UserProfile | null;
  tenant: TenantInfo | null;
  csrfToken: string | null;
  isAuthenticated: boolean;
  /**
   * `true` while we don't yet know whether the user is logged in: during the
   * initial session check, and for as long as the API can't be reached
   * before the first answer. `false` once the API has answered either way.
   */
  isLoading: boolean;
  /** See {@link ConnectionState}. Drives the "Reconnecting…" notice. */
  connection: ConnectionState;
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

interface AuthActions {
  /** Authenticate with email + password. Throws ApiError on failure. */
  login: (payload: LoginPayload) => Promise<void>;
  /**
   * Create a new global account. Does NOT establish a session — the user must
   * verify their email, then sign in. Returns whether the verification email
   * was dispatched. Throws ApiError on failure (e.g. 409 email exists).
   */
  register: (payload: RegisterPayload) => Promise<{ emailSent: boolean }>;
  /**
   * Create the authenticated user's first (or an additional) workspace, then
   * refresh the session so the app routes into it. Throws ApiError on failure
   * (e.g. 409 slug taken).
   */
  createWorkspace: (payload: CreateWorkspacePayload) => Promise<void>;
  /** Invalidate the server session and clear local state. */
  logout: () => Promise<void>;
  /**
   * Validate the current session cookie by calling GET /api/v1/auth/me.
   * Safe to call on every app mount and never throws. A 401/403 signs the
   * user out; any other failure keeps them where they are (a known user stays
   * signed in on screen) and retries in the background with backoff. Resolves
   * after the first attempt; later retries update the store as they land.
   */
  checkAuth: () => Promise<void>;
  /**
   * Start the retries over now, without the loading screen. Used by the
   * notice's "Try again" once the API has been unreachable for a while.
   */
  reconnect: () => void;
  /** Immediately clear local auth state without a server call. */
  clearAuth: () => void;
}

type AuthStore = AuthState & AuthActions;

const SIGNED_OUT = {
  user: null,
  tenant: null,
  csrfToken: null,
  isAuthenticated: false,
  isLoading: false,
  connection: "online",
} as const satisfies Partial<AuthState>;

// Retry bookkeeping lives outside the store: nothing renders from it. Each
// check gets a run number, and a result from an older run (overtaken by a
// newer check, a sign-in or a sign-out) is dropped.
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let checkRun = 0;

/** Cancel any pending retry and make in-flight checks stale. */
function stopSessionChecks(): number {
  if (retryTimer !== null) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
  checkRun += 1;
  return checkRun;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useAuthStore = create<AuthStore>((set, get) => {
  const verifySession = async (run: number, attempt: number): Promise<void> => {
    let meResponse: AuthResponse;
    try {
      meResponse = await api.get<AuthResponse>("/api/v1/auth/me");
    } catch (err) {
      if (run !== checkRun) return;
      if (isSignedOutError(err)) {
        setCsrfToken(null);
        set(SIGNED_OUT);
        return;
      }
      // The API didn't answer. Keep the user where they are: a known user
      // stays signed in on screen, and with nobody known yet we keep waiting.
      const delay = SESSION_RETRY_DELAYS_MS[attempt];
      set({
        connection: delay === undefined ? "unreachable" : "reconnecting",
        isLoading: get().user === null,
      });
      if (delay !== undefined) {
        retryTimer = setTimeout(() => {
          retryTimer = null;
          void verifySession(run, attempt + 1);
        }, delay);
      }
      return;
    }

    // Restore CSRF token after page reload (best-effort — non-fatal on failure)
    let token: string | null = null;
    try {
      const csrfResult = await api.get<{ csrfToken: string }>(
        "/api/v1/auth/csrf-token",
      );
      token = csrfResult.csrfToken;
    } catch {
      /* cache miss or transient error — proceed without CSRF token */
    }
    if (run !== checkRun) return;
    setCsrfToken(token);
    set({
      user: meResponse.user,
      tenant: meResponse.tenant,
      csrfToken: token,
      isAuthenticated: true,
      isLoading: false,
      connection: "online",
    });
  };

  return {
    // Initial state
    user: null,
    tenant: null,
    csrfToken: null,
    isAuthenticated: false,
    isLoading: true,
    connection: "online",

    // Actions
    login: async ({ email, password, tenantSlug }) => {
      const payload: Record<string, string> = { email, password };
      if (tenantSlug !== undefined && tenantSlug !== "") {
        payload["tenantSlug"] = tenantSlug;
      }
      const response = await api.post<AuthResponse>(
        "/api/v1/auth/login",
        payload,
      );
      const token = response.csrfToken ?? null;
      stopSessionChecks();
      setCsrfToken(token);
      set({
        user: response.user,
        tenant: response.tenant,
        csrfToken: token,
        isAuthenticated: true,
        isLoading: false,
        connection: "online",
      });
    },

    register: async (data) => {
      // Public registration creates an account ONLY — no workspace, no session.
      // The user verifies their email, signs in, then creates their workspace.
      const response = await api.post<RegisterResponse>(
        "/api/v1/auth/register",
        data,
      );
      return { emailSent: response.emailSent };
    },

    createWorkspace: async (payload) => {
      await api.post<CreateWorkspaceResponse>("/api/v1/tenants", payload);
      // The server switched the session's active workspace to the new one;
      // refresh user + tenant + roles from /auth/me so routing enters it.
      await get().checkAuth();
    },

    logout: async () => {
      // Best-effort — even if the server call fails we clear local state
      try {
        await api.post("/api/v1/auth/logout");
      } finally {
        stopSessionChecks();
        setCsrfToken(null);
        set(SIGNED_OUT);
      }
    },

    checkAuth: async () => {
      const run = stopSessionChecks();
      set({ isLoading: true });
      await verifySession(run, 0);
    },

    reconnect: () => {
      const run = stopSessionChecks();
      set({ connection: "reconnecting" });
      void verifySession(run, 0);
    },

    clearAuth: () => {
      stopSessionChecks();
      setCsrfToken(null);
      set(SIGNED_OUT);
    },
  };
});

// ---------------------------------------------------------------------------
// Selector hooks — prevents unnecessary re-renders
// ---------------------------------------------------------------------------

/** Returns the currently authenticated user or null. */
export const useCurrentUser = () => useAuthStore((s) => s.user);

/** Returns the current tenant or null. */
export const useCurrentTenant = () => useAuthStore((s) => s.tenant);

/** True while the initial session check is in progress. */
export const useAuthLoading = () => useAuthStore((s) => s.isLoading);

/** True once the session has been confirmed by the server. */
export const useIsAuthenticated = () => useAuthStore((s) => s.isAuthenticated);

/** Whether the session check is reaching the API (see ConnectionState). */
export const useConnectionState = () => useAuthStore((s) => s.connection);

// ---------------------------------------------------------------------------
// Route gate
// ---------------------------------------------------------------------------

/**
 * What the `_authenticated` layout does with the current auth state. Shared
 * by its `beforeLoad` and its component so both follow one rule: only a
 * settled "signed out" (`isLoading` false) sends the user to sign in; while
 * the API can't be reached `isLoading` stays true for an unknown user, so
 * they wait on the loading screen instead.
 */
export type AuthGate = "wait" | "login" | "welcome" | "show";

export function authGate(
  state: Pick<AuthState, "isLoading" | "isAuthenticated" | "tenant">,
): AuthGate {
  if (state.isLoading) return "wait";
  if (!state.isAuthenticated) return "login";
  // Authenticated but no workspace yet (just verified) → finish onboarding.
  if (state.tenant === null) return "welcome";
  return "show";
}
